import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CommentAnchor, ID, User } from '@proofroom/schema';
import { attachmentUrl, threads as threadApi, type ResolvedThread } from '../api/endpoints';
import { keys } from '../api/queries';
import { Chip, EmptyState, formatBytes, timeAgo } from '../components/ui';
import { usePeople } from '../model/people';

export interface ThreadsPanelProps {
  /** A guest reads every thread and may open, reply and attach; resolving and reopening are the users' */
  mode?: 'user' | 'guest';
  documentId: ID;
  draftId: ID;
  threads: ResolvedThread[];
  activeThreadId?: ID;
  onActivate(id: ID | undefined): void;
  storyLabel(storyId: ID | undefined): string;
  pinLabel(anchor: CommentAnchor, n: number): string;
  /** Whose Delete link shows on an attachment: the uploader, or an admin, and nobody else. */
  user: User | null;
}

type Filter = 'open' | 'resolved' | 'all';
const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';

export function ThreadsPanel({ mode = 'user', documentId, draftId, threads, activeThreadId, onActivate, storyLabel, pinLabel, user }: ThreadsPanelProps) {
  const [filter, setFilter] = useState<Filter>('open');
  const isGuest = mode === 'guest';
  const shown = threads.filter((t) => filter === 'all' || t.status === filter);
  const counts = { open: threads.filter((t) => t.status === 'open').length, resolved: threads.filter((t) => t.status === 'resolved').length };
  return (
    <>
      <div className="filter-row" role="tablist">
        {(['open', 'resolved', 'all'] as Filter[]).map((f) => (
          <button key={f} className={`btn sm${filter === f ? ' on' : ' ghost'}`} onClick={() => setFilter(f)}>{f === 'all' ? 'All' : f === 'open' ? `Open ${counts.open}` : `Resolved ${counts.resolved}`}</button>
        ))}
      </div>
      {shown.length === 0 && (
        <EmptyState title={filter === 'open' ? 'No open threads' : 'No threads'} glyph="#">{isGuest ? 'Use the Comment tool to leave a note anywhere on the page.' : 'Select text in the editor and press Comment, or use the Comment tool on the page.'}</EmptyState>
      )}
      {shown.map((t, i) => {
        const n = threads.indexOf(t) + 1;
        return (
          <ThreadCard
            key={t.id} n={n} thread={t} documentId={documentId} draftId={draftId} user={user} canResolve={!isGuest}
            active={t.id === activeThreadId} onActivate={() => onActivate(t.id === activeThreadId ? undefined : t.id)}
            label={t.anchor.storyId ? storyLabel(t.anchor.storyId) : pinLabel(t.anchor, n)} index={i}
          />
        );
      })}
    </>
  );
}

function ThreadCard({ thread, documentId, draftId, active, onActivate, label, n, user, canResolve }: { thread: ResolvedThread; documentId: ID; draftId: ID; active: boolean; onActivate(): void; label: string; index: number; n: number; user: User | null; canResolve: boolean }) {
  const qc = useQueryClient();
  const { nameOf } = usePeople();
  const [reply, setReply] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: keys.threads(documentId, draftId) });
  const comment = useMutation({ mutationFn: (body: string) => threadApi.comment(thread.id, body), onSuccess: () => { setReply(''); void invalidate(); } });
  const question = useMutation({ mutationFn: () => threadApi.question(thread.id, !thread.needsAnswer), onSuccess: () => void invalidate() });
  const resolve = useMutation({ mutationFn: () => threadApi.resolve(thread.id), onSuccess: () => void invalidate() });
  const reopen = useMutation({ mutationFn: () => threadApi.reopen(thread.id), onSuccess: () => void invalidate() });
  const upload = useMutation({ mutationFn: (file: File) => threadApi.uploadAttachment(documentId, thread.id, file), onSuccess: () => void invalidate() });
  const remove = useMutation({ mutationFn: (attachmentId: ID) => threadApi.deleteAttachment(documentId, thread.id, attachmentId), onSuccess: () => void invalidate() });
  const who = (c: ResolvedThread['comments'][number]) => nameOf(c.author, c.authorId);
  const attachments = thread.attachments ?? [];
  return (
    <div className={`thread${active ? ' active' : ''}`} onClick={onActivate}>
      <div className="head">
        <strong style={{ color: 'var(--ink)' }}>#{n}</strong>
        <span>{label}</span>
        <span className="grow" style={{ flex: 1 }} />
        <Chip kind={thread.status}>{thread.status}</Chip>
        {thread.status === 'open' && thread.needsAnswer && <span className="review-warning">Needs answer</span>}
      </div>
      <div className="muted small">Started by {nameOf(thread.createdByUser, thread.createdBy)}{thread.status === 'resolved' && thread.resolvedBy ? `, resolved by ${nameOf(thread.resolvedByUser, thread.resolvedBy)}` : ''}</div>
      {thread.anchor.quote && <div className="quote">“{thread.anchor.quote}”</div>}
      {thread.comments.map((c) => (
        <div key={c.id} className="comment"><span className="who">{who(c)}</span><span className="when">{timeAgo(c.createdAt)}</span><div>{c.body}</div></div>
      ))}
      {attachments.length > 0 && (
        <div className="attachments">
          {attachments.map((a) => {
            const url = attachmentUrl(documentId, thread.id, a.id);
            const canDelete = !!user && (user.id === a.createdBy || user.role === 'admin');
            return (
              <div key={a.id} className="attachment">
                {a.mime.startsWith('image/')
                  ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={a.filename} style={{ maxHeight: 80 }} /></a>
                  : <a href={url} target="_blank" rel="noreferrer">{a.filename}</a>}
                <span className="muted">{formatBytes(a.size)}</span>
                {canDelete && (
                  <button type="button" className="remove" disabled={remove.isPending} onClick={(e) => { e.stopPropagation(); remove.mutate(a.id); }}>Delete</button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {active && (
        <div onClick={(e) => e.stopPropagation()}>
          <div className="reply">
            <input className="input" placeholder="Reply" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && reply.trim()) comment.mutate(reply.trim()); }} />
            <button className="btn sm" disabled={!reply.trim() || comment.isPending} onClick={() => comment.mutate(reply.trim())}>Reply</button>
          </div>
          <div className="btn-row" style={{ marginTop: 8 }}>
            {thread.status === 'open' && <button className="btn sm" disabled={question.isPending} onClick={() => question.mutate()}>{thread.needsAnswer ? 'Mark answered' : 'Mark as question'}</button>}
            {canResolve && (thread.status === 'open'
              ? <button className="btn sm" disabled={resolve.isPending} onClick={() => resolve.mutate()}>Resolve</button>
              : <button className="btn sm" disabled={reopen.isPending} onClick={() => reopen.mutate()}>Reopen</button>)}
            <label className="btn sm ghost">
              {upload.isPending ? 'Attaching…' : 'Attach a file'}
              <input
                type="file" accept={ATTACHMENT_ACCEPT} style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }}
              />
            </label>
            {(question.isError || comment.isError || resolve.isError || reopen.isError || upload.isError || remove.isError) && <span className="error-text">Action failed</span>}
          </div>
        </div>
      )}
    </div>
  );
}
